import { useMemo } from 'react';

import { useResponsive } from '@sg/garnish';

import { useHeaderButtonOptions } from '../../../../../components/Header';
import { NavbarV2 } from '../../../../../components/NavbarV2';
import { defaultHeaderLeft } from '../../../../components/DefaultHeaderLeft';
import {
  CONTENT_WITH_BG_COLOR_STYLES,
  CONTENT_WITHOUT_BG_STYLES,
  SCREEN_HEADER_OPTIONS,
  STATIC_WITH_BORDER_NAVBAR_SCREEN_OPTIONS,
  type ScreenOptions,
  type ScreenOptionsMap,
} from '../../../../constants';
import { useRouteTitles } from '../../../messages';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Screen options for the modals stack.
 */
export const useModalStackOptions = () => {
  const { match } = useResponsive();

  const titles = useRouteTitles();
  const { headerLeftOptions } = useHeaderButtonOptions({ headerLeft: defaultHeaderLeft });

  // ─────────────────────────────────────────────────────────────────────

  const stackOptions = useMemo(() => {
    return {
      ...SCREEN_HEADER_OPTIONS,
      ...headerLeftOptions,
      headerShown: match([false, true]),
      header: match([undefined, renderNavbarV2]),
      presentation: match(['card', 'transparentModal']),
      contentStyle: CONTENT_WITH_BG_COLOR_STYLES,
    } satisfies ScreenOptions;
  }, [headerLeftOptions, match]);

  // ─────────────────────────────────────────────────────────────────────

  return useMemo(() => {
    return {
      //
      // ─── Main Stack Options (applied to all screens) ─────────────────────

      Stack: stackOptions,

      // ─── Individual Screen Options ───────────────────────────────────────

      Bag: { headerShown: false, customDocumentTitle: titles.bagCustomDocumentTitle, contentStyle: CONTENT_WITHOUT_BG_STYLES }, // prettier-ignore
      Reorder:
        { headerShown: false, title: titles.reorderTitle, contentStyle: CONTENT_WITHOUT_BG_STYLES }, // prettier-ignore
      ReorderSubScreen:
        { headerShown: match([true, false]), contentStyle: CONTENT_WITHOUT_BG_STYLES }, // prettier-ignore
      RequestData: { headerShown: false, contentStyle: CONTENT_WITHOUT_BG_STYLES }, // prettier-ignore
      DeliveryOrderInFlight: { headerShown: false, contentStyle: CONTENT_WITHOUT_BG_STYLES }, // prettier-ignore
      Feedback:
        { headerShown: match([true, false]), title: titles.rateOrderTitle, contentStyle: CONTENT_WITHOUT_BG_STYLES }, // prettier-ignore
      OrderProduct: { headerShown: match([true, false]), contentStyle: CONTENT_WITHOUT_BG_STYLES }, // prettier-ignore
      OrderProductConfirmProduct: { headerShown: false, contentStyle: CONTENT_WITHOUT_BG_STYLES },
      OrderProductConfirmLocation: { headerShown: false, contentStyle: CONTENT_WITHOUT_BG_STYLES },
      Chat: { headerShown: false, contentStyle: CONTENT_WITHOUT_BG_STYLES },
      PersonalData: { title: titles.personalDataTitle },
      Customization: STATIC_WITH_BORDER_NAVBAR_SCREEN_OPTIONS,
      DeliveryCustomization: STATIC_WITH_BORDER_NAVBAR_SCREEN_OPTIONS,
      ProductDetails: STATIC_WITH_BORDER_NAVBAR_SCREEN_OPTIONS,
      AddGiftCard: {
        ...STATIC_WITH_BORDER_NAVBAR_SCREEN_OPTIONS,
        headerShown: match([false, true]),
        title: titles.addGiftCardTitle,
      },
      RedeemGiftCard: {
        ...STATIC_WITH_BORDER_NAVBAR_SCREEN_OPTIONS,
        headerShown: match([false, true]),
        title: titles.redeemGiftCardTitle,
      },
      OrderStatus: {
        ...STATIC_WITH_BORDER_NAVBAR_SCREEN_OPTIONS,
        customDocumentTitle: titles.orderStatusCustomDocumentTitle,
      },
      ForgotToScan: {
        customDocumentTitle: titles.forgotToScanCustomDocumentTitle,
      },
      GroupOrderSetup: {
        headerShown: false,
        contentStyle: CONTENT_WITHOUT_BG_STYLES,
        title: titles.groupOrderSetupTitle,
      },
      GroupOrderJoin: {
        headerShown: false,
        contentStyle: CONTENT_WITHOUT_BG_STYLES,
      },
      GroupOrderSearchLocation: {
        headerShown: false,
        contentStyle: CONTENT_WITHOUT_BG_STYLES,
        title: titles.groupOrderLocationSearchTitle,
      },
      GroupOrderShare: {
        headerShown: false,
        contentStyle: CONTENT_WITHOUT_BG_STYLES,
      },
      Loyalty: {
        headerShown: match([false, true]),
        title: titles.loyaltyTitle,
      },
      LocationSelection: {
        headerShown: false,
        title: titles.locationsTitle,
        customDocumentTitle: titles.locationsCustomDocumentTitle,
        contentStyle: CONTENT_WITHOUT_BG_STYLES,
      },
      ShareLineItemLocationSearch: {
        headerShown: false,
        contentStyle: CONTENT_WITHOUT_BG_STYLES,
      },
      Receipt: {
        ...STATIC_WITH_BORDER_NAVBAR_SCREEN_OPTIONS,
        customDocumentTitle: titles.receiptCustomDocumentTitle,
      },
      ClaimReceipt: {
        ...STATIC_WITH_BORDER_NAVBAR_SCREEN_OPTIONS,
        customDocumentTitle: titles.receiptClaimCustomDocumentTitle,
      },
      Credit: {
        headerShown: match([false, true]),
        title: titles.creditAndPromoCodesTitle,
      },
      CreditAdd: {
        ...STATIC_WITH_BORDER_NAVBAR_SCREEN_OPTIONS,
        headerShown: match([false, true]),
        title: titles.creditAddTitle,
      },
      CaDataRequests: {
        ...STATIC_WITH_BORDER_NAVBAR_SCREEN_OPTIONS,
        title: titles.personalDataTitle,
      },
      DietaryRestrictions: { headerShown: true, title: titles.dietaryRestrictionsTitle }, // prettier-ignore
      ShareLineItem: {
        ...STATIC_WITH_BORDER_NAVBAR_SCREEN_OPTIONS,
        navbarLayout: 'floating',
      },
      QuickAdd: {
        headerShown: false,
        contentStyle: CONTENT_WITHOUT_BG_STYLES,
        presentation: 'transparentModal',
        animation: 'fade',
      },
      QuickAddLocationSearch: {
        headerShown: false,
        contentStyle: CONTENT_WITHOUT_BG_STYLES,
      },
      Bundle: {
        headerShown: false,
        presentation: match(['fullScreenModal', 'transparentModal']),
        contentStyle: match([CONTENT_WITH_BG_COLOR_STYLES, CONTENT_WITHOUT_BG_STYLES]),
      },
      BundleLocationSearch: {
        headerShown: false,
        contentStyle: CONTENT_WITHOUT_BG_STYLES,
      },
      HsaFsa: {
        ...STATIC_WITH_BORDER_NAVBAR_SCREEN_OPTIONS,
        headerShown: match([false, true]),
        title: titles.hsaFsaTitle,
        customDocumentTitle: titles.hsaFsaCustomDocumentTitle,
      },
      CartHandoff: {
        headerShown: false,
        contentStyle: CONTENT_WITHOUT_BG_STYLES,
        presentation: 'transparentModal',
        animation: 'fade',
      },
    } satisfies ScreenOptionsMap;
  }, [
    match,
    stackOptions,
    titles.addGiftCardTitle,
    titles.bagCustomDocumentTitle,
    titles.creditAddTitle,
    titles.creditAndPromoCodesTitle,
    titles.dietaryRestrictionsTitle,
    titles.forgotToScanCustomDocumentTitle,
    titles.groupOrderLocationSearchTitle,
    titles.groupOrderSetupTitle,
    titles.loyaltyTitle,
    titles.locationsCustomDocumentTitle,
    titles.locationsTitle,
    titles.orderStatusCustomDocumentTitle,
    titles.personalDataTitle,
    titles.rateOrderTitle,
    titles.redeemGiftCardTitle,
    titles.reorderTitle,
    titles.receiptCustomDocumentTitle,
    titles.receiptClaimCustomDocumentTitle,
    titles.hsaFsaTitle,
    titles.hsaFsaCustomDocumentTitle,
  ]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function renderNavbarV2() {
  return <NavbarV2 />;
}
